import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables, TablesInsert } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type OvertimePayout = Tables<"overtime_payouts">;
export type OvertimePayoutInsert = TablesInsert<"overtime_payouts">;

type ZeroOvertimePayout = {
  id: string;
  personID: string;
  date: number | string;
  hours: number;
  notes?: string | null;
  createdBy?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? "";
}

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyPayout(row: ZeroOvertimePayout): OvertimePayout {
  return {
    id: row.id,
    person_id: row.personID,
    date: toLegacyDate(row.date),
    hours: row.hours,
    notes: row.notes ?? null,
    created_by: row.createdBy ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useOvertimePayouts(
  personId: string | undefined,
  year?: number,
) {
  const query = personId
    ? appQueries.overtimePayouts.byPerson({ personID: personId, year })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!personId });
  const payouts = ((data ?? []) as ZeroOvertimePayout[]).map(toLegacyPayout);

  return {
    data: payouts,
    isLoading:
      !!personId && details.type !== "complete" && payouts.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateOvertimePayout() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (payout: OvertimePayoutInsert) => {
    const input = {
      id: payout.id ?? generateUUID(),
      personID: payout.person_id,
      date: payout.date,
      hours: payout.hours,
      notes: payout.notes ?? null,
      createdBy: payout.created_by ?? null,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.overtimePayouts.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Overtime payout create was rejected",
      );
      return toLegacyPayout(input);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      payout: OvertimePayoutInsert,
      options?: MutationOptions<OvertimePayout>,
    ) => {
      void run(payout)
        .then((data) => {
          toast.success("Overtime payout recorded");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to record payout: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteOvertimePayout() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.overtimePayouts.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Overtime payout delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Payout deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete payout: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
